import { compose, equals, head, prop, sortBy, tail, toUpper } from 'ramda';
import type { QueryMutationError } from '@bo-types/common';
import type { TableColumn } from '@library/types/utils';

export const findElementIndexInObjectList = <
  T,
  Key extends keyof T,
  V extends T[Key]
>(
  list: Array<T>,
  matchKey: Key,
  value: V
): number => list.findIndex((item) => item[matchKey] === value);

export const getErrorMessage = ({
  isPublishProjectionsError,
  publishProjectionsError
}: {
  isPublishProjectionsError: boolean;
  publishProjectionsError: QueryMutationError;
}) => {
  let errorMessage = '';
  const updateErrorMessage = (message: string) => {
    errorMessage =
      (errorMessage ? `${errorMessage}; ` : errorMessage) + message;
  };

  if (isPublishProjectionsError) {
    updateErrorMessage(
      publishProjectionsError && 'message' in publishProjectionsError
        ? (publishProjectionsError.message as string)
        : 'Create Projections Error'
    );
  }

  return errorMessage;
};

export const isValidArray = (array: unknown) =>
  Array.isArray(array) && array.length > 0;

export const isValidArrayWithValues = (array: unknown, key?: string) =>
  Array.isArray(array) &&
  array.length > 0 &&
  array.every((value) =>
    key ? Boolean((value as Record<string, unknown>)[key]) : Boolean(value)
  );

export const sortByKey = <T>(data: Array<T>, key: string): Array<T> => {
  const sortByKeyCaseInsensitive = sortBy(compose(prop(key)));
  return sortByKeyCaseInsensitive(data) as T[];
};

export const isEqualArrays = <T>(
  a: Array<T> | null,
  b: Array<T> | null,
  key: string
) => {
  if (Array.isArray(a) && Array.isArray(b) && a.length === b.length) {
    return equals(sortByKey(a, key), sortByKey(b, key));
  }

  return false;
};

export const removeDuplicatesInArray = <T>(arr: Array<T>): Array<T> => {
  return arr.filter((item, index) => arr.indexOf(item) === index);
};

export const delayPromise = (ms: number) => {
  let timeoutId;

  const promise = new Promise((resolve) => {
    timeoutId = setTimeout(resolve, ms);
  });

  return {
    timeoutId,
    promise
  };
};

export const formatLabelByKey = (
  key: string,
  uppercaseAcronyms: Array<string> = []
) => {
  return key
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) =>
      uppercaseAcronyms.includes(word)
        ? toUpper(word)
        : toUpper(head(word) || '') + tail(word)
    )
    .join(' ');
};

export const formatToCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
};

export const formatToSnakeCase = (str: string): string => {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase();
};

export const formatVariableToLabel = (str: string) => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (s) => s.toUpperCase());
};

export const copyTextToClipboard = async <T extends HTMLElement>(
  event: React.MouseEvent<T, MouseEvent>,
  text: string
) => {
  event.stopPropagation();

  await navigator.clipboard.writeText(text);

  const { promise } = delayPromise(2000);
  await promise;

  return text;
};

export const removeUnderscore = (str: string) => {
  return str.replaceAll('_', ' ');
};

export const dropEmptyParams = (
  params: Record<string, unknown>
): Record<string, unknown> => {
  Object.keys(params).forEach((key) => {
    if (params[key] === '') {
      delete params[key];
    }
  });

  return params;
};

export const mapColumnWidthsToCells = ({
  headers,
  tableData
}: {
  headers: Array<TableColumn>;
  tableData: Record<string, unknown>;
}) => {
  const columnWidths: Record<string, string> = {};

  headers.forEach(({ column, width }) => {
    columnWidths[column] = width || 'w-[10%]';
  });

  return {
    ...tableData,
    columnWidths
  };
};

export const toTitleCase = (phrase: string, delimiter = ' ') =>
  phrase
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(delimiter);

// Automatically formats as currency + truncates extra significant digits
export const toUSD = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
